<template>
    <v-dialog v-model="dialog" max-width="740" scrollable content-class="fb-dialog-root">
        <div class="fb-dialog">
            <!-- HEADER -->
            <header class="fb-header">
                <div class="fb-header__icon">
                    <v-icon size="20">mdi-credit-card-off-outline</v-icon>
                </div>
                <div class="fb-header__text">
                    <h2 class="fb-header__title">חיובים שלא נגבו</h2>
                    <p class="fb-header__sub">{{ customerName || 'הלקוח' }}</p>
                </div>
                <button type="button" class="fb-close" @click="dialog = false" aria-label="סגירה">
                    <v-icon size="18">mdi-close</v-icon>
                </button>
            </header>

            <!-- SUMMARY -->
            <section class="fb-summary" aria-label="סיכום החוב">
                <div class="fb-summary__item">
                    <span class="fb-summary__label">סה״כ לתשלום</span>
                    <strong class="fb-summary__value fb-summary__value--debt">{{ formatCurrency(totalSum) }}</strong>
                </div>
                <div class="fb-summary__divider"></div>
                <div class="fb-summary__item">
                    <span class="fb-summary__label">מספר חיובים</span>
                    <strong class="fb-summary__value">{{ sortedBills.length }}</strong>
                </div>
                <div class="fb-summary__divider"></div>
                <div class="fb-summary__item">
                    <span class="fb-summary__label">החיוב הוותיק ביותר</span>
                    <strong class="fb-summary__value">{{ oldestLabel }}</strong>
                </div>
            </section>

            <!-- LIST -->
            <div class="fb-body">
                <article v-for="(bill, i) in sortedBills" :key="bill.RowID || i" class="fb-card">
                    <div class="fb-card__top">
                        <div class="fb-card__title-wrap">
                            <h3 class="fb-card__title">{{ bill.DocumentDescription || 'חיוב ללא תיאור' }}</h3>
                            <div class="fb-card__tags">
                                <span class="fb-tag" :class="statusClass(bill)">{{ statusLabel(bill) }}</span>
                                <span v-if="overdueDays(bill) > 0" class="fb-tag fb-tag--overdue">
                                    <v-icon class="fb-tag__icon">mdi-calendar-alert</v-icon>
                                    באיחור {{ overdueDays(bill) }} ימים
                                </span>
                                <span v-if="bill.BillingAttempts > 1" class="fb-tag fb-tag--attempts">
                                    <v-icon class="fb-tag__icon">mdi-reload-alert</v-icon>
                                    {{ bill.BillingAttempts }} ניסיונות גבייה
                                </span>
                            </div>
                        </div>

                        <div class="fb-card__amount">
                            <strong>{{ formatCurrency(bill.SumToBill) }}</strong>
                            <span v-if="bill.SumToBillNoVat">{{ formatCurrency(bill.SumToBillNoVat) }} לפני מע״מ</span>
                        </div>
                    </div>

                    <div v-if="bill.failedMessage" class="fb-reason">
                        <v-icon size="16" class="fb-reason__icon">mdi-credit-card-remove-outline</v-icon>
                        <div class="fb-reason__text">
                            <span class="fb-reason__label">סיבת הסירוב</span>
                            <span class="fb-reason__value">{{ bill.failedMessage }}</span>
                        </div>
                    </div>

                    <dl class="fb-meta">
                        <div class="fb-meta__item">
                            <dt>מועד חיוב מתוכנן</dt>
                            <dd>{{ formatDate(bill.OriginalNextDate || bill.CreateDate) }}</dd>
                        </div>
                        <div class="fb-meta__item">
                            <dt>ניסיון גבייה אחרון</dt>
                            <dd>{{ formatDateTime(bill.LastDate) }}</dd>
                        </div>
                        <div class="fb-meta__item" v-if="bill.PaymentNum">
                            <dt>מספר תשלום</dt>
                            <dd>{{ bill.PaymentNum }}</dd>
                        </div>
                        <div class="fb-meta__item" v-if="bill.RecurringId">
                            <dt>הוראת קבע</dt>
                            <dd class="fb-num">{{ bill.RecurringId }}</dd>
                        </div>
                        <div class="fb-meta__item" v-if="bill.TranzactionId">
                            <dt>מזהה עסקה</dt>
                            <dd class="fb-num">{{ bill.TranzactionId }}</dd>
                        </div>
                        <div class="fb-meta__item" v-if="bill.Quantity">
                            <dt>כמות</dt>
                            <dd>{{ Number(bill.Quantity) }} × {{ formatCurrency(bill.UnitPrice) }}</dd>
                        </div>
                    </dl>
                </article>
            </div>

            <!-- FOOTER -->
            <footer class="fb-footer">
                <p class="fb-footer__note">
                    <v-icon size="15" class="ml-1">mdi-information-outline</v-icon>
                    הנתונים מגיעים ממערכת הסליקה ומתעדכנים בכל ניסיון גבייה.
                </p>
                <button type="button" class="fb-btn" @click="dialog = false">סגירה</button>
            </footer>
        </div>
    </v-dialog>
</template>

<script>
export default {
    props: {
        value: { type: Boolean, default: false },
        bills: { type: Array, default: () => [] },
        customerName: { type: String, default: '' },
    },
    computed: {
        dialog: {
            get() { return this.value; },
            set(val) { this.$emit('input', val); },
        },
        sortedBills() {
            return [...this.bills].sort((a, b) => {
                const dateA = new Date(a.OriginalNextDate || a.CreateDate || 0).getTime();
                const dateB = new Date(b.OriginalNextDate || b.CreateDate || 0).getTime();
                return dateA - dateB;
            });
        },
        totalSum() {
            return this.bills.reduce((sum, bill) => sum + (Number(bill.SumToBill) || 0), 0);
        },
        oldestLabel() {
            const oldest = this.sortedBills[0];
            if (!oldest) return '—';
            return this.formatDate(oldest.OriginalNextDate || oldest.CreateDate);
        },
    },
    methods: {
        formatCurrency(value) {
            const num = Number(value) || 0;
            return num.toLocaleString('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },
        formatDate(value) {
            if (!value) return '—';
            const d = new Date(value);
            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('he-IL');
        },
        formatDateTime(value) {
            if (!value) return '—';
            const d = new Date(value);
            if (isNaN(d.getTime())) return '—';
            return `${d.toLocaleDateString('he-IL')} ${d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
        },
        overdueDays(bill) {
            const value = bill.OriginalNextDate || bill.CreateDate;
            if (!value) return 0;
            const target = new Date(value);
            if (isNaN(target.getTime())) return 0;
            target.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.max(0, Math.round((today.getTime() - target.getTime()) / 86400000));
        },
        statusLabel(bill) {
            const map = {
                DEBTAUTOBILLING: 'חוב - חיוב אוטומטי',
                DEBT: 'חוב',
                FAILED: 'גבייה נכשלה',
                PENDING: 'ממתין לגבייה',
                CANCELED: 'בוטל',
            };
            return map[bill.Status] || bill.Status || 'גבייה נכשלה';
        },
        statusClass(bill) {
            return bill.Status === 'PENDING' ? 'fb-tag--pending' : 'fb-tag--debt';
        },
    },
};
</script>

<style scoped>
.fb-dialog {
    --fb-navy: #0d2c6d;
    --fb-red: #c74747;
    --fb-red-deep: #a52f2f;
    --fb-red-soft: rgba(199, 71, 71, 0.10);
    --fb-border: #e3e8ef;
    --fb-border-soft: #eef2f7;
    --fb-text: #0f172a;
    --fb-text-soft: #475569;
    --fb-text-mute: #94a3b8;
    --fb-bg: #f6f8fb;
    --fb-ease: cubic-bezier(0.22, 1, 0.36, 1);
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    border-radius: 18px;
    overflow: hidden;
    background: #fff;
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: var(--fb-text);
}

/* HEADER */
.fb-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: linear-gradient(135deg, var(--fb-red-deep) 0%, var(--fb-red) 60%, #d15c5c 100%);
    color: #fff;
}

.fb-header__icon {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.25);
}

.fb-header__icon ::v-deep .v-icon,
.fb-close ::v-deep .v-icon {
    color: #fff !important;
}

.fb-header__text {
    flex: 1;
    min-width: 0;
}

.fb-header__title {
    margin: 0;
    font-size: 1.08rem;
    font-weight: 800;
    line-height: 1.25;
}

.fb-header__sub {
    margin: 2px 0 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.8rem;
    font-weight: 600;
}

.fb-close {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    transition: background 0.18s var(--fb-ease);
}

.fb-close:hover {
    background: rgba(255, 255, 255, 0.28);
}

/* SUMMARY */
.fb-summary {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 13px 20px;
    background: var(--fb-bg);
    border-bottom: 1px solid var(--fb-border);
}

.fb-summary__item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

.fb-summary__divider {
    width: 1px;
    height: 26px;
    background: var(--fb-border);
}

.fb-summary__label {
    color: var(--fb-text-mute);
    font-size: 0.68rem;
    font-weight: 700;
}

.fb-summary__value {
    color: var(--fb-text);
    font-size: 0.95rem;
    font-weight: 800;
    font-feature-settings: "tnum" 1;
}

.fb-summary__value--debt {
    color: var(--fb-red);
    font-size: 1.12rem;
}

/* LIST */
.fb-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px 4px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fb-card {
    padding: 14px 15px;
    border-radius: 14px;
    border: 1px solid var(--fb-border-soft);
    background: #fff;
    box-shadow: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    border-inline-start: 3px solid var(--fb-red);
    transition: box-shadow 0.18s var(--fb-ease);
}

.fb-card:hover {
    box-shadow: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
}

.fb-card__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
}

.fb-card__title-wrap {
    min-width: 0;
}

.fb-card__title {
    margin: 0 0 7px;
    font-size: 0.94rem;
    font-weight: 700;
    line-height: 1.35;
}

.fb-card__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.fb-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
}

.fb-tag__icon {
    font-size: 13px !important;
    color: inherit !important;
}

.fb-tag--debt {
    color: var(--fb-red);
    background: var(--fb-red-soft);
}

.fb-tag--pending {
    color: #c0691c;
    background: #fff3df;
}

.fb-tag--overdue {
    color: #8a3f00;
    background: #ffeacc;
}

.fb-tag--attempts {
    color: #4a5568;
    background: #eef2f7;
}

.fb-card__amount {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex-shrink: 0;
    text-align: start;
}

.fb-card__amount strong {
    color: var(--fb-red);
    font-size: 1.16rem;
    font-weight: 800;
    font-feature-settings: "tnum" 1;
    line-height: 1.1;
}

.fb-card__amount span {
    color: var(--fb-text-mute);
    font-size: 0.68rem;
    font-weight: 600;
}

.fb-reason {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
    padding: 9px 11px;
    border-radius: 10px;
    background: var(--fb-red-soft);
    border: 1px solid rgba(199, 71, 71, 0.18);
}

.fb-reason__icon {
    color: var(--fb-red) !important;
    margin-top: 1px;
    flex-shrink: 0;
}

.fb-reason__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.fb-reason__label {
    color: var(--fb-red);
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.01em;
}

.fb-reason__value {
    color: #7a2222;
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.35;
    word-break: break-word;
}

.fb-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 9px 14px;
    margin: 12px 0 0;
    padding-top: 11px;
    border-top: 1px dashed var(--fb-border);
}

.fb-meta__item dt {
    color: var(--fb-text-mute);
    font-size: 0.67rem;
    font-weight: 700;
    margin-bottom: 2px;
}

.fb-meta__item dd {
    margin: 0;
    color: var(--fb-text-soft);
    font-size: 0.8rem;
    font-weight: 700;
}

.fb-num {
    direction: ltr;
    text-align: start;
    font-feature-settings: "tnum" 1;
}

/* FOOTER */
.fb-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 13px 20px;
    background: var(--fb-bg);
    border-top: 1px solid var(--fb-border);
}

.fb-footer__note {
    display: flex;
    align-items: center;
    margin: 0;
    color: var(--fb-text-mute);
    font-size: 0.73rem;
    font-weight: 600;
}

.fb-footer__note ::v-deep .v-icon {
    color: var(--fb-text-mute) !important;
}

.fb-btn {
    padding: 7px 22px;
    border-radius: 10px;
    background: var(--fb-navy);
    color: #fff;
    font-size: 0.83rem;
    font-weight: 700;
    font-family: inherit;
    transition: background 0.18s var(--fb-ease), transform 0.18s var(--fb-ease);
}

.fb-btn:hover {
    background: #142f78;
    transform: translateY(-1px);
}

@media (max-width: 620px) {
    .fb-card__top {
        flex-direction: column;
        gap: 8px;
    }

    .fb-summary {
        flex-wrap: wrap;
        gap: 10px 14px;
    }

    .fb-summary__divider {
        display: none;
    }
}
</style>